import {useState} from 'react'
import {Link} from 'react-router-dom'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.email, values.password)
        saveAuth(auth)
        const {data: user} = await getUserByToken(auth.api_token)
        setCurrentUser(user)
      } catch (error) {
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '16px',
        padding: 'clamp(28px, 7vw, 48px) clamp(20px, 6vw, 40px)',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 14px 44px rgba(27,27,41,0.1)',
        border: '1px solid #eef0f4',
      }}
    >
      {/* Shield icon + title */}
      <div style={{textAlign: 'center', marginBottom: '32px'}}>
        <img
          src='/app-icon.png'
          alt='App Icon'
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '16px',
            marginBottom: '20px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            objectFit: 'cover',
          }}
        />
        <h1 style={{fontSize: '24px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px'}}>
          Crew Crashpad
        </h1>
        <p style={{fontSize: '14px', color: '#64748b', margin: 0}}>Sign in to your account</p>
      </div>

      {/* Error status */}
      {formik.status && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#dc2626',
            fontSize: '14px',
          }}
        >
          {formik.status}
        </div>
      )}

      <form onSubmit={formik.handleSubmit} noValidate>
        {/* Email */}
        <div style={{marginBottom: '20px'}}>
          <label style={{display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
            Email Address
          </label>
          <div style={{position: 'relative'}}>
            <div
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
                <polyline points='22,6 12,13 2,6' />
              </svg>
            </div>
            <input
              type='email'
              placeholder='admin@example.com'
              autoComplete='off'
              {...formik.getFieldProps('email')}
              style={{
                width: '100%',
                padding: '12px 14px 12px 44px',
                border: `1px solid ${formik.touched.email && formik.errors.email ? '#f87171' : '#e5e7eb'}`,
                borderRadius: '8px',
                fontSize: '14px',
                color: '#1e293b',
                outline: 'none',
                boxSizing: 'border-box',
                background: '#f9fafb',
              }}
            />
          </div>
          {formik.touched.email && formik.errors.email && (
            <p style={{color: '#dc2626', fontSize: '12px', margin: '4px 0 0'}}>{formik.errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div style={{marginBottom: '20px'}}>
          <label style={{display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px'}}>
            Password
          </label>
          <div style={{position: 'relative'}}>
            <div
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
                <path d='M7 11V7a5 5 0 0 1 10 0v4' />
              </svg>
            </div>
            <input
              type='password'
              autoComplete='off'
              placeholder='••••••••'
              {...formik.getFieldProps('password')}
              style={{
                width: '100%',
                padding: '12px 14px 12px 44px',
                border: `1px solid ${formik.touched.password && formik.errors.password ? '#f87171' : '#e5e7eb'}`,
                borderRadius: '8px',
                fontSize: '14px',
                color: '#1e293b',
                outline: 'none',
                boxSizing: 'border-box',
                background: '#f9fafb',
              }}
            />
          </div>
          {formik.touched.password && formik.errors.password && (
            <p style={{color: '#dc2626', fontSize: '12px', margin: '4px 0 0'}}>{formik.errors.password}</p>
          )}
          <div style={{textAlign: 'right', marginTop: '8px'}}>
            <Link to='/auth/forgot-password' style={{fontSize: '13px', color: '#039540', fontWeight: 600, textDecoration: 'none'}}>
              Forgot password?
            </Link>
          </div>
        </div>

        {/* Remember me + Forgot password */}
        {/* <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px'}}>
          <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
            <input
              type='checkbox'
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer'}}
            />
            <span style={{fontSize: '13px', color: '#374151'}}>Remember me</span>
          </label>
          <a href='#' style={{fontSize: '13px', color: '#3b82f6', textDecoration: 'none', fontWeight: 500}}>
            Forgot password?
          </a>
        </div> */}

        {/* Submit */}
        <button
          type='submit'
          disabled={formik.isSubmitting || !formik.isValid}
          style={{
            width: '100%',
            padding: '13px',
            background: formik.isSubmitting || !formik.isValid
              ? '#7ec79a'
              : 'linear-gradient(135deg, #039540, #027a34)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: formik.isSubmitting || !formik.isValid ? 'not-allowed' : 'pointer',
            transition: 'opacity 0.2s',
            letterSpacing: '0.3px',
          }}
        >
          {loading ? (
            <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
              <span
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#ffffff',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              Please wait...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p style={{textAlign: 'center', fontSize: '14px', color: '#64748b', margin: '22px 0 0'}}>
        Don't have an account?{' '}
        <Link to='/auth/registration' style={{color: '#039540', fontWeight: 600, textDecoration: 'none'}}>
          Sign up
        </Link>
      </p>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
